// Fact-based timeline PDF — direct jsPDF draw (no html2canvas capture) so
// every glyph is crisp vector text. Mirrors the in-app Written timeline:
// date headings, each record's subject + parties, and its verified facts with
// verbatim quotes. No AI-generated prose — every line is record metadata or a
// source-verified quote.

import { jsPDF } from "jspdf";
import type { Case, RecordFact, TimelineRecord } from "../types";
import { slugify, triggerDownload } from "./exportShared";
import { factMeta } from "./factMeta";
import {
  formatDate as _formatDate,
  formatDateTime as _formatDateTime,
} from "./formatDate";
import { SOURCE_TYPE_LABELS, SIGNIFICANCE_LABELS } from "../types";

interface Bundle {
  caseData: Case;
  records: TimelineRecord[];
  generatedAt: string;
  // Match the on-screen toggles: coverage_term and background facts are
  // excluded unless the corresponding toggle is on. Default off.
  includeCoverage?: boolean;
  includeBackground?: boolean;
}

const MARGIN = 54;

const COLOR_TEXT: [number, number, number] = [25, 25, 25];
const COLOR_MUTED: [number, number, number] = [110, 95, 70];
const COLOR_HEADING: [number, number, number] = [20, 20, 20];
const COLOR_BORDER: [number, number, number] = [215, 205, 180];
const COLOR_QUOTE: [number, number, number] = [70, 70, 70];
const COLOR_DATE: [number, number, number] = [60, 110, 190];

function formatShortDate(iso: string): string {
  return _formatDate(iso, "short") || iso;
}
function formatPreparedDate(iso: string): string {
  return _formatDateTime(iso) || iso;
}

function sourceLabel(type: string): string {
  return (SOURCE_TYPE_LABELS as Record<string, string>)[type] ?? "Record";
}

// Facts shown in the export, matching the on-screen toggles: case_event and
// valuation always; coverage_term / background only when requested.
function exportFacts(
  rec: TimelineRecord,
  includeCoverage: boolean,
  includeBackground: boolean,
): RecordFact[] {
  return rec.facts.filter((f) => {
    if (f.fact_category === "background") return includeBackground;
    if (f.fact_category === "coverage_term") return includeCoverage;
    return true;
  });
}

function factTagLabel(category: string | undefined): string | null {
  if (category === "coverage_term") return "Coverage term";
  if (category === "background") return "Background";
  return null;
}

export async function exportTimelinePdf(bundle: Bundle): Promise<void> {
  const {
    caseData,
    records,
    generatedAt,
    includeCoverage = false,
    includeBackground = false,
  } = bundle;
  const factsOf = (rec: TimelineRecord) =>
    exportFacts(rec, includeCoverage, includeBackground);
  const sorted = [...records].sort((a, b) => a.date.localeCompare(b.date));

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  const contentWidth = w - MARGIN * 2;
  const bottomMargin = MARGIN + 30;
  let y = MARGIN;

  function addPage(): void {
    doc.addPage();
    y = MARGIN;
  }
  function ensure(space: number): void {
    if (y + space > h - bottomMargin) addPage();
  }
  function writeLines(
    text: string,
    opts: {
      font?: "normal" | "bold" | "italic";
      size: number;
      color: [number, number, number];
      lineHeight: number;
      indent?: number;
    },
  ): void {
    doc.setFont("helvetica", opts.font ?? "normal");
    doc.setFontSize(opts.size);
    doc.setTextColor(...opts.color);
    const indent = opts.indent ?? 0;
    const lines = doc.splitTextToSize(text, contentWidth - indent);
    for (const line of lines) {
      ensure(opts.lineHeight);
      doc.text(line, MARGIN + indent, y + opts.size * 0.9);
      y += opts.lineHeight;
    }
  }

  // --- Header ---------------------------------------------------------------
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...COLOR_HEADING);
  for (const line of doc.splitTextToSize(caseData.name, contentWidth)) {
    doc.text(line, MARGIN, y + 14);
    y += 22;
  }
  const factCount = sorted.reduce((n, r) => n + factsOf(r).length, 0);
  writeLines(
    `Timeline · ${sorted.length} record${sorted.length === 1 ? "" : "s"} · ${factCount} verified fact${factCount === 1 ? "" : "s"} · prepared ${formatPreparedDate(generatedAt)}`,
    { size: 10, color: COLOR_MUTED, lineHeight: 14 },
  );
  y += 6;
  ensure(12);
  doc.setDrawColor(...COLOR_BORDER);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, y, w - MARGIN, y);
  y += 16;

  // --- Records grouped by date ---------------------------------------------
  let lastDate = "";
  for (const rec of sorted) {
    // Group by calendar day — record_date carries a time component.
    if (rec.date.slice(0, 10) !== lastDate) {
      lastDate = rec.date.slice(0, 10);
      y += 6;
      writeLines(formatShortDate(rec.date), {
        font: "bold",
        size: 12,
        color: COLOR_DATE,
        lineHeight: 16,
      });
    }

    // Subject.
    writeLines(rec.subject?.trim() || "(no subject)", {
      font: "bold",
      size: 11,
      color: COLOR_HEADING,
      lineHeight: 14,
      indent: 12,
    });

    // Parties + type + significance.
    const parties = [rec.from, rec.to].filter(Boolean).join(" → ");
    const sig =
      rec.significance === "high"
        ? ` · ${SIGNIFICANCE_LABELS.high}`
        : "";
    const metaLine = `${parties ? parties + "  " : ""}(${sourceLabel(rec.type)})${sig}${
      rec.content_trust === "low" ? "  ⚠ Transcription" : ""
    }`;
    writeLines(metaLine, {
      size: 9.5,
      color: COLOR_MUTED,
      lineHeight: 12,
      indent: 12,
    });

    // Facts.
    for (const f of factsOf(rec)) {
      const amount = f.amount ? `  [${f.amount}]` : "";
      const tag = factTagLabel(f.fact_category);
      writeLines(
        `• ${tag ? `[${tag}] ` : ""}${factMeta(f.fact_type).label}: ${f.summary}${amount}`,
        {
        size: 10,
        color: COLOR_TEXT,
        lineHeight: 13,
        indent: 20,
      });
      writeLines(`“${f.verbatim_quote}”`, {
        font: "italic",
        size: 9.5,
        color: COLOR_QUOTE,
        lineHeight: 12,
        indent: 30,
      });
    }
    if (rec.attachment_count > 0) {
      writeLines(
        `📎 ${rec.attachment_count} attachment${rec.attachment_count === 1 ? "" : "s"}`,
        { size: 9, color: COLOR_MUTED, lineHeight: 12, indent: 20 },
      );
    }
    y += 8;
  }

  // --- Footer on every page -------------------------------------------------
  const total = doc.getNumberOfPages();
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...COLOR_MUTED);
  const footer = `Generated by CaseThread · ${formatShortDate(new Date().toISOString())}`;
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.text(footer, MARGIN, h - 20);
    doc.text(`Page ${i} of ${total}`, w - MARGIN, h - 20, { align: "right" });
  }

  const blob = doc.output("blob");
  const filename = `CaseThread - ${slugify(caseData.name)} - timeline - ${new Date()
    .toISOString()
    .slice(0, 10)}.pdf`;
  triggerDownload(blob, filename);
}
